import normaxBrand from "../../assets/brand/normax-brand.svg";
import "./BrandLogo.css";

export interface BrandLogoProps {
  /** sm = compact header contexts; md = card/prominent contexts. */
  size?: "sm" | "md";
  /** dark = dark wordmark for light surfaces (default). light = white wordmark for navy surfaces. */
  tone?: "dark" | "light";
  className?: string;
}

/**
 * NormaX wordmark — artwork vendored from normax.rs
 * (assets/brand/normax-brand.svg, viewBox "15 30 810 180").
 *
 * `tone="dark"` (default): an <img> with the navy wordmark, for light
 * surfaces (AppShell header, Login card). An <img src=".svg"> renders in
 * secure-static mode and does NOT inherit CSS `color`, so the file keeps
 * its real navy fill (#0A2239) baked in.
 *
 * `tone="light"`: the same 11 paths inlined here with fill="currentColor"
 * so CSS `color` sets the mark colour — for navy surfaces (the sidebar
 * lockup, Task 13's BootSplash). This repo has no vite-plugin-svgr, so the
 * markup is inlined as JSX rather than imported via `?react`. Spec §5.
 */
export function BrandLogo({ size = "md", tone = "dark", className }: BrandLogoProps) {
  const cls = ["nx-brand-logo", `nx-brand-logo--${size}`, `nx-brand-logo--${tone}`, className]
    .filter(Boolean)
    .join(" ");
  if (tone === "light") {
    return (
      <svg
        className={cls}
        viewBox="15 30 810 180"
        role="img"
        aria-label="NormaX Performance Intelligence"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>NormaX Performance Intelligence</title>
        <path
          d="M 38.5 37 L 60.5 37 Q 61.25 38.75 64.5 38 L 71.5 42 L 77 47.5 L 84 56.5 L 152 132.5 L 154 135.5 L 154 138.5 L 132.5 165 Q 128 166 127 163.5 L 103 134.5 L 65.5 93 L 64 93.5 L 65 91.5 Q 64 89 63 90.5 L 63 202.5 L 60.5 205 L 32.5 205 L 27.5 203 L 24 199.5 L 23 197.5 L 23 50.5 L 26 44.5 L 30.5 40 L 38.5 37 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 133.5 71 L 174.5 71 L 178.5 73 L 182 76.5 L 200 99.5 L 205 104.5 L 205 120 L 217.5 120 L 220 122.5 L 240 147.5 L 247 154.5 L 262 173.5 L 283 197.5 L 286 201.5 L 286 203.5 L 284.5 205 L 245.5 205 L 244.5 204 L 239.5 203 L 233 196.5 L 229 190.5 L 200.5 157 L 163.5 202 L 156.5 205 L 117.5 205 Q 115.5 204.5 116 201.5 L 177 127.5 L 134 76.5 L 132 72.5 L 133.5 71 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 204.5 104 L 217 119.5 L 204 120 L 204.5 104 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 249.5 37 L 294.5 37 Q 295.25 36.75 295 39.5 L 233.5 113 L 229 113 L 229 111.5 L 210 89.5 L 208 86.5 L 208 83.5 L 239 45.5 L 245.5 39 L 249.5 37 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 793.5 64 L 817.5 64 Q 818.25 63.75 818 66.5 L 785.5 107 L 782 104.5 L 773 92.5 L 773 89.5 L 791.5 65 L 793.5 64 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 327.5 71 L 344.5 71 L 350 75.5 L 386.5 124 L 387 123.5 L 387 71 L 405.5 71 L 406 71.5 L 406 154.5 L 404.5 156 L 387.5 156 L 381 149.5 L 370 133.5 L 366 129.5 L 352 109.5 L 345.5 102 L 345 102.5 L 345 156 L 326.5 156 L 326 155.5 L 326 72.5 L 327.5 71 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 733.5 81 L 754.5 81 L 759 85.5 L 762 90.5 L 770 99.5 L 772 103.5 L 807 147.5 L 812 154.5 L 812 156 L 802.5 157 L 801.5 156 L 788.5 156 L 786 153.5 L 769.5 131 L 750.5 156 L 738.5 156 L 737.5 157 L 729.5 157 Q 727.5 156.5 728 153.5 L 744 133.5 L 753 120.5 L 757 116.5 L 757 114.5 L 753 110.5 L 732 82.5 L 733.5 81 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 432.5 91 L 469.5 91 L 470.5 92 L 473.5 92 L 475.5 93 L 483 100.5 Q 482.5 103 484 103.5 L 484 109.5 Q 486 110.5 485 114.5 L 484 115.5 L 484 122.5 L 485 123.5 L 485 134.5 L 484 136.5 Q 486 137.5 484 138.5 L 484 143.5 L 481 149.5 L 478.5 152 L 470.5 156 L 461.5 156 Q 460.5 158 456.5 157 L 455.5 156 L 430.5 156 L 424.5 153 L 421 149.5 L 418 144.5 Q 418.75 141.25 417 140.5 L 417 106.5 L 418 105.5 L 418 102.5 L 419 100.5 L 423.5 95 L 428.5 92 Q 431.75 92.75 432.5 91 Z M 441 107 Q 440 109 437 108 L 436 111 L 436 137 L 441 140 L 462 140 L 464 139 L 466 136 L 466 112 L 465 110 L 462 107 L 441 107 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 509.5 91 L 543 91 L 543 107 L 518.5 107 Q 517.58 108.75 515 108 L 514 110.5 L 514 154.5 L 512.5 156 L 499.5 156 L 497.5 157 L 495 156 L 495 104.5 L 497 99.5 L 503.5 93 L 505.5 92 L 508.5 92 L 509.5 91 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 561.5 91 L 635.5 91 L 642.5 94 L 647 98.5 L 650 106.5 L 650 156 L 637.5 156 Q 637 157.5 634.5 157 Q 633.58 155.25 631 156 L 631 110.5 L 628.5 108 L 626.5 107 L 610 107 L 610 156 L 591 156 L 591 109.5 L 589.5 108 L 587.5 107 L 571.5 107 L 570 108.5 L 570 154.5 L 568.5 156 L 551.5 156 L 551 155.5 L 551 100.5 L 553 96.5 L 556.5 93 L 561.5 91 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
        <path
          d="M 662 91 L 706.5 91 L 707.5 92 L 710.5 92 L 712.5 93 L 719 98.5 L 722 104.5 L 722 156 L 671.5 156 L 665.5 153 L 662 149.5 L 659 143.5 L 659 127.5 L 662 121.5 L 665.5 118 L 673.5 115 L 701.5 115 L 702 114.5 L 701.5 113 Q 703.75 112.25 703 114.5 L 704 114.5 L 704 111.5 L 701.5 108 L 699.5 107 L 663.5 107 L 662 105.5 L 662 91 Z M 680 130 L 677 134 L 677 138 Q 678 140 680 138 L 679 140 L 704 140 L 704 130 L 680 130 Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  return <img src={normaxBrand} alt="NormaX Performance Intelligence" className={cls} />;
}
